import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RotateCcw, Upload, ImageIcon } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const pub = (p) => (p ? `${window.location.origin}/api/public/img/${p}` : null);

const SLOTS = [
  ["slide_1", "Diapositive 1", "Présentation du cabinet"],
  ["slide_2", "Diapositive 2", "Ressources humaines"],
  ["slide_3", "Diapositive 3", "Gestion externalisée"],
  ["slide_4", "Diapositive 4", "Tarifs (flyer)"],
  ["slide_5", "Diapositive 5", "Comptabilité & paie"],
  ["slide_6", "Diapositive 6", "Informatisation"],
  ["slide_7", "Diapositive 7", "Fiscalité"],
  ["apropos", "À propos", "Photo de la section À propos"],
];

export default function AdminSite() {
  const [photos, setPhotos] = useState(null);
  const [busyKey, setBusyKey] = useState(null);

  const load = () =>
    api.get("/public/content")
      .then((r) => setPhotos(r.data.photos || {}))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  useEffect(() => {
    load();
  }, []);

  const upload = async (key, file) => {
    if (!file) return;
    setBusyKey(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/photos/${key}`, fd);
      toast.success("Photo mise à jour : elle s'affiche maintenant sur le site.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusyKey(null);
    }
  };

  const reset = async (key) => {
    if (!window.confirm("Revenir à la photo par défaut pour cet emplacement ?")) return;
    setBusyKey(key);
    try {
      await api.delete(`/admin/photos/${key}`);
      toast.success("Photo par défaut rétablie.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div data-testid="admin-site-view">
      <h2 className="font-display text-xl font-bold text-korban-navy">Photos du site</h2>
      <p className="mt-2 text-sm text-slate-500">
        Changez les photos du diaporama et de la section « À propos ». Choisissez des images professionnelles,
        de préférence larges (paysage) et lumineuses. JPG, PNG ou WEBP — 10 Mo maximum.
      </p>
      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        {SLOTS.map(([key, label, hint]) => {
          const current = photos?.[key];
          return (
            <div key={key} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid={`photo-slot-${key}`}>
              <div className="relative h-40 bg-slate-100">
                {current ? (
                  <img src={pub(current)} alt={label} className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full flex-col items-center justify-center gap-2 text-slate-400">
                    <ImageIcon size={26} />
                    <p className="text-xs font-semibold">Photo par défaut</p>
                  </div>
                )}
                {busyKey === key && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/70">
                    <Loader2 size={22} className="animate-spin text-korban-navy2" />
                  </div>
                )}
              </div>
              <div className="p-4">
                <p className="text-sm font-bold text-korban-navy">{label}</p>
                <p className="text-xs text-slate-500">{hint}</p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg bg-korban-navy px-3.5 py-2 text-xs font-bold text-korban-gold transition-all hover:-translate-y-0.5 hover:bg-korban-navy2">
                    <Upload size={13} /> {current ? "Changer" : "Téléverser"}
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.webp,.gif"
                      className="hidden"
                      data-testid={`photo-input-${key}`}
                      onChange={(e) => upload(key, e.target.files?.[0])}
                    />
                  </label>
                  {current && (
                    <button
                      onClick={() => reset(key)}
                      data-testid={`photo-reset-${key}`}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3.5 py-2 text-xs font-bold text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50"
                    >
                      <RotateCcw size={13} /> Photo par défaut
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
